import { formatLocalDate } from '../../utils/localDateTime';
import React from 'react';
import { GlobalThreatGroupAnalysisResult } from '@types';
import { Section, Disclaimer, RISK_LEVEL_COLORS } from './ReportShared';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import AnalysisMarkdown from './AnalysisMarkdown';
import ThreatGroupSupplement from './ThreatGroupSupplement';
import { getMapCoordinates } from '../../utils/mapCoordinates';
import { threatGroupMapDetails } from '../../utils/threatGroupMapDetails';
import { prepareThreatGroupReport, threatGroupSections } from '../../../functions/src/common/threatGroupReportText';

interface GlobalThreatGroupDisplayProps {
    result: GlobalThreatGroupAnalysisResult;
}

const GlobalThreatGroupDisplay: React.FC<GlobalThreatGroupDisplayProps> = ({ result }) => {
    result = prepareThreatGroupReport(result);
    const { isLoaded } = useGoogleMaps();
    const riskColor = RISK_LEVEL_COLORS[result.threatLevel] || RISK_LEVEL_COLORS['Medium'];

    // Map markers for activities and operational areas
    const activityMarkers = (result.recentActivities || []).flatMap((act, idx) => {
        const coordinates = getMapCoordinates(act);
        return coordinates ? [{
            ...act,
            ...threatGroupMapDetails(act),
            id: `act-${idx}`,
            ...coordinates,
            name: `${act.type}: ${formatLocalDate(act.date, act.date)}`,
            title: `${act.type} (${formatLocalDate(act.date, act.date)})`,
            type: 'SecurityIncident',
            description: act.description,
            summary: act.description,
            color: '#dc2626'
        }] : [];
    });

    const operationalMarkers = (result.areaOfOperations?.locations || []).flatMap((loc, idx) => {
        const coordinates = getMapCoordinates(loc);
        return coordinates ? [{
            ...loc,
            ...threatGroupMapDetails(loc),
            id: `loc-${idx}`,
            ...coordinates,
            name: loc.name,
            title: `Zone: ${loc.name}`,
            type: 'ThreatGroup',
            description: `Operational node for ${result.groupName}`,
            summary: `Operational node for ${result.groupName}`,
            color: '#000000'
        }] : [];
    });

    const tacticalMarkers = (result.mapMarkers || []).flatMap((marker, index) => {
        const coordinates = getMapCoordinates(marker);
        return coordinates ? [{ ...marker, ...threatGroupMapDetails(marker), ...coordinates,
            id: `tactical-${index}`, title: marker.name, summary: marker.description }] : [];
    });
    const allMarkers = [...activityMarkers, ...operationalMarkers, ...tacticalMarkers];
    const initialCenter = getMapCoordinates(result.areaOfOperations)
        || (allMarkers.length > 0 ? { lat: allMarkers[0].lat, lng: allMarkers[0].lng } : { lat: 20, lng: 0 });

    const pmesiiData = result.pmesiiAnalysis || result.pmesii || {};
    const carverData = result.carverAssessment || result.carver || {};

    return (
        <div className="threat-group-report space-y-8 animate-in fade-in duration-500">
            {/* Header / Branding */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-4 border-b-2 border-slate-900 pb-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-widest uppercase">SRM Intelligence Dossier</span>
                        <span className="text-slate-500 font-bold text-[10px] tracking-widest uppercase">Registry: TG-OSINT-{(result.groupName || 'UNK').substring(0, 3).toUpperCase()}</span>
                    </div>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tight uppercase leading-none">
                        {result.groupName || 'Unidentified Group'}
                    </h2>
                    <div className="flex items-center gap-4 mt-3">
                        <div className={`px-3 py-1 rounded text-xs font-black uppercase border ${riskColor.bg} ${riskColor.text} ${riskColor.border}`}>
                            Current Threat: {result.threatLevel}
                        </div>
                        <div className="text-sm text-slate-500 font-bold uppercase tracking-wider">
                            Primary Region: {result.countryOfOperation}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-end">
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-right">
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Dossier Status</div>
                        <div className="text-emerald-600 font-black text-sm flex items-center gap-1.5 uppercase">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            Active Monitoring
                        </div>
                    </div>
                </div>
            </div>

            {/* Executive Summary */}
            <div id={threatGroupSections.summary.id} className="report-print-section bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight mb-3">Operational Intelligence Summary</h3>
                <AnalysisMarkdown value={result.summary || (result as any).description} className="text-slate-700 leading-relaxed font-medium" />
                <div id={threatGroupSections.profile.id} className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Strategic Ideology</h4>
                        <AnalysisMarkdown value={result.ideology} className="text-sm text-slate-700 font-semibold" />
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Historical Context</h4>
                        <AnalysisMarkdown value={result.historicalContext} className="text-sm text-slate-700 leading-relaxed italic line-clamp-3" />
                    </div>
                </div>
            </div>

            {result.assetRiskAnalysis && (
                <Section id={threatGroupSections.asset.id} title="Asset-Specific Risk Assessment">
                    <AnalysisMarkdown value={result.assetRiskAnalysis} className="text-sm text-slate-700" />
                </Section>
            )}

            {/* Geospatial Component */}
            <Section title="Geospatial Activity & Operational Zones" className="report-map-threat-section">
                <div className="report-map-threat-layout flex flex-col lg:flex-row gap-6 h-[500px]">
                    <div className="report-map-threat-map flex-grow rounded-xl overflow-hidden border border-slate-300 relative shadow-inner">
                        <UnifiedMap
                            isLoaded={isLoaded}
                            initialViewState={{ 
                                longitude: initialCenter.lng ?? 0, 
                                latitude: initialCenter.lat ?? 0, 
                                zoom: result.areaOfOperations?.zoom || 5 
                            }}
                            theme="light"
                            sidebarTitle={`THREAT ACTIVITY: ${result.groupName}`}
                            markers={allMarkers}
                            choroplethEnabled={false}
                            riskData={[]}
                        />
                    </div>
                    <div className="report-map-threat-list w-full lg:w-80 bg-slate-900 rounded-xl p-4 overflow-y-auto custom-scrollbar border border-slate-800">
                        <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-4">Tactical Events</h4>
                        <div className="space-y-3">
                            {result.recentActivities?.map((act, i) => (
                                <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-[10px] font-black text-red-400 uppercase">{act.type}</span>
                                        <span className="text-[9px] text-slate-500 font-bold">{formatLocalDate(act.date, act.date || '')}</span>
                                    </div>
                                    <AnalysisMarkdown value={act.description} className="text-xs text-slate-300 font-medium leading-snug" />
                                </div>
                            ))}
                            {!result.recentActivities?.length && (
                                <div className="text-center py-8 text-slate-600 text-xs italic">
                                    No recorded kinetic activities in this period.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Section>

            {/* Leadership */}
            {(result.keyLeaders?.length || 0) > 0 && (
                <Section id={threatGroupSections.leaders.id} title="Leadership Hierarchy & Command">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {result.keyLeaders?.map((leader, i) => (
                            <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-black text-slate-900 text-sm uppercase leading-tight">{leader.name}</h4>
                                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-tighter">{leader.role}</span>
                                    </div>
                                </div>
                                <AnalysisMarkdown value={leader.bio} className="text-xs text-slate-600 leading-relaxed italic" />
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* PMESII & CARVER Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Section id={threatGroupSections.pmesii.id} title="PMESII-PT Operational Variables">
                    <div className="bg-slate-900 rounded-xl p-6 border border-slate-800 shadow-xl space-y-4">
                        {Object.entries(pmesiiData).map(([key, val]) => (
                            <div key={key} className="border-b border-slate-800 pb-3 last:border-0 last:pb-0">
                                <h5 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">{key}</h5>
                                <AnalysisMarkdown value={val || 'No data recorded for this variable.'} className="text-xs text-slate-300 leading-relaxed" />
                            </div>
                        ))}
                    </div>
                </Section>

                <Section id={threatGroupSections.carver.id} title="CARVER Target Assessment">
                    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {['criticality', 'accessibility', 'recuperability', 'vulnerability', 'effect', 'recognizability'].map((key) => (
                                <div key={key} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                    <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{key}</h5>
                                    <AnalysisMarkdown value={(carverData as any)[key]} className="text-xs text-slate-700 font-bold leading-tight" />
                                </div>
                            ))}
                        </div>
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100 mt-2">
                            <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Synthesized Targeting Outlook</h5>
                            <AnalysisMarkdown value={(carverData as any)?.overallAssessment || (carverData as any)?.analysis || 'Assessment pending operational data verification.'} className="text-sm text-indigo-900 font-semibold italic" />
                        </div>
                    </div>
                </Section>
            </div>
            {result.ascopeAnalysis && (
                <Section id={threatGroupSections.ascope.id} title="Civil Considerations (ASCOPE)">
                    <div className="grid gap-4 md:grid-cols-2">
                        {Object.entries(result.ascopeAnalysis).map(([key, value]) => (
                            <div key={key} className="rounded-lg border border-slate-200 p-4">
                                <h3 className="font-semibold capitalize">{key}</h3>
                                <AnalysisMarkdown value={value} className="text-sm text-slate-700" />
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            <ThreatGroupSupplement result={result} />

            <Disclaimer className="threat-group-footer" text={result.disclaimer || "This threat group dossier is generated by Insights using open-source intelligence and academic grounding. It is designed for strategic planning and situational awareness."} />
        </div>
    );
};

export default GlobalThreatGroupDisplay;
